import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  Input,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PlayerModel } from '@modules/media/models/player.model';
import { TranscriptEntryModel } from '@modules/media/models/transcript-entry.model';
import { TranscriptModel, TranscriptNavigationMode } from '@modules/media/models/transcript.model';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';
import { Option } from '@modules/ui/interfaces/option.interface';

import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { SelectComponent } from '../../ui/select/select.component';

import { TranscriptContentComponent } from './transcript-content/transcript-content.component';
import { TranscriptSectionSelectionComponent } from './transcript-section-selection/transcript-section-selection.component';
import { TranscriptTagSelectionComponent } from './transcript-tag-selection/transcript-tag-selection.component';

@Component({
  selector: 'mb-transcript',
  templateUrl: './transcript.component.html',
  styleUrls: ['./transcript.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranscriptSectionSelectionComponent,
    TranscriptTagSelectionComponent,
    SelectComponent,
    TranscriptContentComponent,
    DropdownComponent,
    ActionContainerComponent,
    TranslatePipe,
  ],
})
export class TranscriptComponent implements OnInit, OnDestroy {
  @ViewChild('speakerWrapperInner', { static: false })
  speakerWrapperInner!: ElementRef;

  private navigationHide = false;
  private videoInterval: any = null;
  private destroy$: Subject<void> = new Subject<void>();

  @Input() isMobile = false;
  @Input() transcript!: TranscriptModel;
  @Input() player!: PlayerModel;

  @Output() langChanged = new EventEmitter();
  @Output() closeTranscript = new EventEmitter();
  @Output() openMedia = new EventEmitter();

  public transcriptActions: ButtonItem[] = [{ icon: ActionIcons.BOOKMARK }];
  public mobileFilters: Option[] = [];
  public autoScroll = false;
  public synced = true;
  public synced$ = new ReplaySubject<boolean>();
  public currentEntry: TranscriptEntryModel | null = null;
  public activeEntry = new Subject<TranscriptEntryModel>();
  public sections: string[] = [];
  public activeSection: string | null = null;
  public entries: TranscriptEntryModel[] = [];

  // private methods
  private getCurrentEntry() {
    const time = this.videoTime;
    const index = this.entries.findIndex((e) => e.time + this.transcript.offset >= time);
    if (index > -1) {
      return this.entries[index ? index - 1 : index];
    }
    return this.entries?.length ? this.entries[this.entries.length - 1] : null;
  }

  private get videoTime() {
    return this.player.currentTime * 1000;
  }
  // public methods
  public sync() {
    this.synced = true;
    this.autoScroll = true;
    const currentEntry = this.getCurrentEntry();
    if (!currentEntry) {
      this.activeSection = null;
      return;
    }
    this.currentEntry = currentEntry;
    this.activeEntry.next(currentEntry);

    this.activeSection =
      this.transcript.navMode === TranscriptNavigationMode.SPEAKER
        ? this.currentEntry.speaker?.fullname
        : this.currentEntry.chapter;
  }

  public sectionChanged(section: string) {
    this.synced = false;
    this.activeSection = section;
    this.autoScroll = true;
    const activeEntry = this.transcript.getCurrentEntryBySection(section);
    this.activeEntry.next(activeEntry);
  }

  public contentScrolled() {
    this.synced = false;
  }

  public filtersSelectChanged(filtes: Option[]) {
    this.filtersChanged(filtes.map((filter) => (filter.selected ? filter.value : false)).filter(Boolean));
  }
  public filtersChanged(filters: string[]) {
    if (!filters.length) {
      this.sync();
    }
    this.transcript.setFilters(filters);
  }

  ngOnInit() {
    // update current entry depending on video time
    // FIXME: only run this, when the player is running, clean this mess up

    // once entries are loaded, we can start updating the currently active entry
    this.videoInterval = setInterval(() => {
      // only run this when the videoTime actually changes
      if (this.transcript.time != this.videoTime) {
        this.transcript.time = this.videoTime;
        this.updateActiveAndCurrentEntry();
      }
    }, 500);

    // FIXME: use observables for current entry as well?
    this.transcript.entries.pipe(takeUntil(this.destroy$)).subscribe((entries) => {
      this.entries = entries;
      this.updateActiveAndCurrentEntry();
    });

    this.transcript.sections$.pipe(takeUntil(this.destroy$)).subscribe((sections) => {
      this.sections = sections;
    });
  }

  ngOnDestroy() {
    clearInterval(this.videoInterval);

    this.destroy$.next();
    this.destroy$.complete();

    if (this.navigationHide) {
      document.body.classList.remove('hideNavigation');
      this.navigationHide = false;
    }
  }

  private updateActiveAndCurrentEntry() {
    const currentEntry = this.getCurrentEntry();
    if (!currentEntry) {
      return;
    }
    this.currentEntry = currentEntry;
    if (this.synced) {
      this.activeSection =
        this.transcript.navMode === TranscriptNavigationMode.CHAPTER
          ? this.currentEntry.chapter
          : this.currentEntry.speaker?.fullname;
      this.activeEntry.next(currentEntry);
      return;
    }
  }
}
